import { Basic } from "../basic.ts";
import { ChartMenu } from "./menu/menu.js";
import { ChartIntervals } from "./intervals/intervals.js";
import { ChartGroups } from "./groups/groups.js";
import { UiModal } from '../common/modal/modal.js';
import { ChartColumnWorker } from '../../observer/chartColumnWorker.js';
import { State } from './state.js';
import { SelectTree } from './popups/tree/tree.js';
import { generateFileName } from "../../lib/utils.js";
import { getCalendarDate } from '../common/utils/date.js'
import { getFormattedValue } from "../../lib/utils.js";
import i18next from "i18next";
import styles from './chartcolumn.css'

const generatePrintTemplatre = (elem) => `
    <!DOCTYPE html>
    <style>
        @media print {
            ${!(window.ipc && window.clientPrinter)
                ? `
                    @page {
                        size: A4 landscape;
                    }
                ` : ""
            }
            img {
                max-width: 100%; /* нужно, чтобы изображение не выходило за пределы окна */
            }
        }
    </style>
    <html>
        <head>
            <title>${i18next.t("chartcolumn_print_trend")}</title>
        </head>
        <body>
            <img src="${elem}">
        </body>
    </html>
`

/**
 * @func getTimestamp
 * @classdesc Столбчатая диаграмма
 * @param {number} endTime время конца в мс
 * @param {number} ammount кол-во времени в periodType
 * @param {string} periodType один из значений 'min', 'hou', 'day', 'mon', 'yea'
 * @returns {number} timestamp начала
 * */

const dependencies = [
    "groupname",
    "data",
    "division",
    "archivestart",
    "archiveend",
    "fillingconstructionarea",
    "backgroundgrid",
    "gridcolor",
    "standardlistintervals",
    "pengroupslist",
    "legend",
    "menuonoff",
    "setuppengroup",
    "setupinterval",
    "setupdatatype",
    "setupcustomization",
    "resetsetup",
    "imageexport",
    "chartprinting",
    "xaxiscolor",
    "italicxaxis",
    "xaxisboldness",
    "xaxisunderlining",
    "autoscaling",
    "max",
    "min",
    "yaxiscolor",
    "yaxislabel",
    "penmaxammount",
    "italicsignature",
    "underliningsignature",
    "exportnameformat",
];

const template = () => `
    <style>
        ${styles}
    </style>
    <div id="chartcolumn-inner" class="chartcolumn-inner-wrap">
        <div class="navigation" id="navigation">
            <div class="navigation-content">
                <div id="menu-container" class="menu-container"></div>
                <div id="groups-container"></div>
            </div>
            <div id="intervals-container"></div>
        </div>
    </div>
`;

/**
 * @class MSChartColumn
 * @extends Basic
 * @classdesc Столбчатая диаграмма
 * */
export class MSChartColumn extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat(dependencies);
    }
    /**
     * @constructor
     * */
    constructor() {
        super();
        const shadowRoot = this.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = template();
        this.checkComponent('ui-modal', UiModal)
        this.initDefaults();
    }

    /**
     * Инициализация групп
    * */
    initGroups() {
        if (this.pengroupslist) {
            const container = this.shadowRoot.getElementById('groups-container')
            this.groups = new ChartGroups(container, this)
        } else if (this.groups) this.groups.container.textContent = ''
    }

    /**
     * Инициализация стандартных интервалов
    * */
    initIntervals() {
        if (this.standardlistintervals) {
            const container = this.shadowRoot.getElementById('intervals-container')
            this.intervals = new ChartIntervals(container, this)
        } else if (this.intervals) this.intervals.container.textContent = ''
    }

    /**
     * Инициализация меню
    * */
    initMenu() {
        if (this.menuonoff) {
            const container = this.shadowRoot.getElementById('menu-container')
            this.menu = new ChartMenu(container, this)
        } else if (this.menu) this.menu.container.textContent = ''
    }

    /**
     * Инициализация значений по дефолту
    * */
    initDefaults() {
        this.state = new State(this)
        this.is_chartcolumn = true
        this.root = this.shadowRoot.getElementById("chartcolumn-inner");
        this.navigation = this.shadowRoot.querySelector('#navigation');
        this._groupname = "";
        this._standardlistintervals = true;
        this._pengroupslist = true;
        this._borderthickness = 1;
        this._bordercolor = 'gray';
        this._backgroundcolor = '#FFFFFF';
        this._opacity = 1;
        this._borderstyle = 0;
        this.shadowsize = 0;
        this._shadowcolor = '#FFFFFF';
        this._backgroundtile = 2;
        this._isvisible = true;
        this._isenabled = true;
        this._mousecursorkind = 0;
        this._menuonoff = true;
        this._setuppengroup = true;
        this._setupinterval = true;
        this._resetsetup = true;
        this._imageexport = true;
        this._chartprinting = true;
        this._italicxaxis = false;
        this._xaxisboldness = false;
        this._penmaxammount = 5;
        this._italicsignature = true;
        this._underliningsignature = false;
        this.parentHasPopup = this.parentElement.closest('ms-popup') !== null;
        this.afterSwitch = this.afterSwitch.bind(this);
        window.addEventListener("ms_message", this.afterSwitch);
    }

    /**
     * Инициализация компонента столбчатой диаграммы
    * */
    initChart() {
        if (!this.echarts) {
            const params = {
                renderer: 'svg',
            };
            this.echarts = echarts.init(this.root, null, params);
            this.echarts.setOption(this.state.echarts);
            this.echarts.on('legendselectchanged', this.onLegendChange.bind(this))
        }
    }

    /**
     * @func initSelectTree
     * @param {any} новая созданная группа
    */
    initSelectTree(group, isCreate) {
        if (!this._tree) this._tree = new SelectTree(this)
        const currentGroup = group ?? this.state.groups.find(g => g.id === this.state.selectedGroup)
        if(isCreate){
            this._tree.onUpdate = (newSources) => {
                if ($pm.hasPermissions) {
                const options = {
                    cancel: () => { },
                    customText: `${i18next.t("chartcolumn_creating_a_group_of_feathers")}: ${this.elname}`
                };
                $pm.resolveControl(
                    this,
                    "PenGroupsCreate",
                    () => this._tree.onUpdate = this.onSelectTreeUpdate.call(this, currentGroup)(newSources),
                    options
                );
            } else this._tree.onUpdate = this.onSelectTreeUpdate.call(this, currentGroup)(newSources)
            }
        } else {
            this._tree.onUpdate = this.onSelectTreeUpdate.call(this, currentGroup)
        }
        this._tree.group = currentGroup
        this._tree.root = this
        const sources = currentGroup.selected.map(g => g.source)
        this._tree.Open(currentGroup.name, 1, sources);
    }

    onSelectTreeUpdate(group) {
        return (newSources) => {
            if (!newSources.length && !group.selected.length) {
                this.state.groups = this.state.groups.filter(item => item.id !== group.id)
                this.groups.values = this.state.groups
                return
            }
            for (let i = 0; i < newSources.length; i++) {
                if (this.state.selectedSources.length < this.penmaxammount) this.state.selectedSources.push(newSources[i])
                else {
                    const pen = this.state.pens.find(pen => pen.path === newSources[i])
                    const name = pen?.name ?? newSources[i].split('.').pop()
                    $ns.add({
                        type: "warning",
                        time: new Date().toLocaleString(),
                        title: i18next.t("chartcolumn_chart_warning"),
                        text: String.format(i18next.t("diagram_pens_limit"), name),
                    });
                }
            }
            this.groups.value = group.id
            this.groups.dropdown?.createList?.()
        }
    }

    onEmptySelect(group) {
        return (newSources) => {
            if (!newSources.length && !group.selected.length) {
                this.state.groups = this.state.groups.filter(item => item.id !== group.id)
                this.groups.values = this.state.groups
            }
        }
    }

    afterSwitch(message) {
        const data = message.data;
        switch (data.event) {
            case "afterswitch":
                this.state.resetState()
                this.menu.updateTreeValues()
                this.menu.intervalElem?.updateCopyValues()
                this.menu.intervalElem?.render()
                this.state.initEchartsState()
                this.state.initGroups();
                this.initializeAndFetchHistory();
                this.intervals.labelElem.innerHTML = i18next.t("chartcolumn_your_interval")
                this.setPm();
                break;
        }
    }

    setPm() {
        if ($pm.hasPermissions) {
            const penGroupsCreate = $pm.checkAllow(
                $pm.getEntryFromElement(this, "PenGroupsCreate")
            );
            this.pmPenGroupsCreate = penGroupsCreate

            const pensSettings = $pm.checkAllow(
                $pm.getEntryFromElement(this, "TrendChangePens")
            );
            this.pmPensSettings = pensSettings

            const intervalsSettings = $pm.checkAllow(
                $pm.getEntryFromElement(this, "IntervalsSettings")
            );
            this.pmIntervalsSettings = intervalsSettings

            const standardIntervals = $pm.checkAllow(
                $pm.getEntryFromElement(this, "StandardIntervalsSelect")
            );
            this.pmStandardIntervals = standardIntervals

            const imageExport = $pm.checkAllow(
                $pm.getEntryFromElement(this, "TrendSave")
            );
            this.pmImageExport = imageExport

            const print = $pm.checkAllow(
                $pm.getEntryFromElement(this, "TrendPrint")
            );
            this.pmPrint = print
        } else {
            this.pmPenGroupsCreate = true
            this.pmPensSettings = true
            this.pmIntervalsSettings = true
            this.pmStandardIntervals = true
            this.pmImageExport = true
            this.pmPrint = true
        }
    }

    async initializeAndFetchHistory(){
        await this.setDefaultGroupPathes()
        this.setDefaultIntervalState()
        this.setDefaultGroupState()
        this.getHistoryData()
    }

    changeGroupHandler(e) {
        const li = e.target.closest('li')
        const Id = li?.getAttribute('Id')
        if (!li || !Id) return
        const selectedGroup = this.state.groups.find(g => g.id === Id)
        this.state.selectedSources = selectedGroup.selected.map(g => g.source)
        this.groups.value = Id
    }

    updateGroupState() {
        const group = this.state.groups.find(g => g.id === this.state.selectedGroup)
        group.selected = []
        this.state.echarts.series = []
        this.state.echarts.legend.data = []
        this.state.echarts.legend.selected = {}
        this.getNewSources()
        for (let i = 0; i < this.state.pens.length; i++) {
            const pen = this.state.pens[i]
            if (!this.state.selectedSources.includes(pen.source)) pen.hidden = true
            else if (this.state.echarts.series.length < this.penmaxammount) {
                pen.hidden = false
                this.state.echarts.series.push(pen.serie)
                this.state.echarts.legend.data.push(pen.name)
                this.state.echarts.legend.selected[pen.name] = pen.active
                const { serie, ...rest } = pen
                group.selected.push(rest)
            }
            else {
                pen.hidden = true
                $ns.add({
                    type: "warning",
                    time: new Date().toLocaleString(),
                    title: i18next.t("chartcolumn_chart_warning"),
                    text: String.format(i18next.t("diagram_pens_limit"), pen.name),
                });
            }
        }
        this.state.selectedSources = []
        this.menu?.pensElem?.render?.()
        this.groups?.saveState?.()
        this.getHistoryData()
    }

    getNewSources() {
        const newSources = []
        const oldSources = this.state.pens.map(pen => pen.source)
        for (let i = 0; i < this.state.selectedSources.length; i++) {
            if (!oldSources.includes(this.state.selectedSources[i])) newSources.push(this.state.selectedSources[i])
        }
        if (newSources.length) this.state.createSourcePens(newSources)
    }

    onLegendChange({ name, selected }) {
        const elem = this.state.pens.find(item => item.name === name)
        elem.active = selected[name]
        this.menu?.pensElem?.render()
    }

    async initWorker() {
        try {
            this.worker = new ChartColumnWorker(`chartcolumn_${this.id}`)
            this.worker.setHistoryData = this.setHistoryData.bind(this)
        } catch (e) {
            console.log('initWorker ERROR: ', e)
        }
    }

    async getHistoryData() {
        try {
            const pens = this.state.pens.filter(pen => !pen.hidden && (pen.itemId || pen.path))
            await this.worker.CreateReportSubscription(
                this.archivestart,
                this.archiveend,
                this.division,
                pens,
                this.resampleInterval
            )
            await this.worker.PublishReportData()
        } catch (e) {
            console.log('getHistoryData ERROR: ', e)
        }
    }

    async getSourcePath() {
        const sources = this.state.params
        const response = await this.worker.GetArchiveItems(sources)
        if(!response) return
        for (let i = 0; i < response.data.items.length; i++) {
            const item = this.state.pens.find(item => item.itemId === sources[i].itemId && item.path === sources[i].path)
            if (item) item.source = response.data.items[i].fullName
            else sources[i].source = response.data.items[i].fullName
        }
    }

    setHistoryData(rows, columns) {
        const [_, ...rest] = columns
        columns = rest.map(item => +item.name)
        const xAxis = this.state.echarts.xAxis.data = []
        const pens = this.state.pens
        this.state.cleanPenSerieData()
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            if (!row.length) continue
            const [timestamp, ...rest] = row
            xAxis.push(timestamp)
            rest.forEach((value, i) => pens[columns[i]] && pens[columns[i]].serie.data.push(value))
        }
        this.updateChart()
    }

    updateChart() {
        const { yAxis } = this.state.echarts
        const autoscateValue = this.autoscaling ? { min: null, max: null } : { min: this.state.min, max: this.state.max }
        this.echarts.setOption({ ...this.state.echarts, yAxis: { ...yAxis, ...autoscateValue } }, true)
    }

    changeIntervalHandler(e) {
        const li = e.target.closest('li')
        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: `${i18next.t("chartcolumn_selecting_standart_intervals")} : ${this.elname}`,
            };
            $pm.resolveControl(
                this,
                "StandardIntervalsSelect",
                this._changeIntervalHandler.bind(this, li),
                options
            );
        } else this._changeIntervalHandler(li);
    }

    _changeIntervalHandler(li) {
        const Id = li?.getAttribute('Id')
        const Value = +li?.getAttribute('Value')
        if (!li || !Id) return
        this.intervals.value = Id;
        let [start, end] = [0, 0]
        switch (Value) {
            case 0:
                [start, end] = getCalendarDate(0)
                this.division = 4
                this.resampleInterval = 1
                break
            case 1:
                [start, end] = getCalendarDate(1)
                this.division = 4
                this.resampleInterval = 1
                break
            case 2:
                [start, end] = getCalendarDate(2)
                this.division = 3
                this.resampleInterval = 7
                break
            case 3:
                [start, end] = getCalendarDate(3)
                this.division = 3
                this.resampleInterval = 1
                break
            default:
                [start, end] = getCalendarDate(0)
                this.division = 4
                this.resampleInterval = 12
        }
        this.archivestart = start
        this.archiveend = end
        this.getHistoryData()
    }

    addDataSource(param, path) {
        const index = +path.split('.').at(-2)
        const pen = this.state.pens[index]
        if (!pen) return
        pen.itemId = param.itemId
        pen.path = param.path
        this.state.params.push(param)
    }

    onStringPenChanged(propertyPath, value) {
        if(typeof value === 'string'){
        const [pen] = this.state.createSourcePens([value], { hidden: false, source: value })
        this.state.stringPens.push(structuredClone(pen))
        this.menu?.pensElem?.render?.()
        this.getHistoryData()
        }
    }

    resetSettings() {
        this.state.resetState()
        const defaultGroup = this.state.groups.find(g => g.id === 'default')
        this.state.selectedSources = defaultGroup.selected.map(g => g.source)
        this.groups.value = 'default'
        this.menu.updateTreeValues()
        this.menu.intervalElem?.updateCopyValues()
        this.menu.intervalElem?.render()
        this.saveItemState({
            activeGroupId: undefined,
            groups: undefined,
            division: undefined,
            startDate: undefined,
            endDate: undefined
        }
        )
    }

    printChart() {
        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.clientPrinter
                ? `${this.elname} ${i18next.t("chartcolumn_print_to_printer")} ${window.PrinterName}`
                    : `${this.elname} ${i18next.t("chartcolumn_print_diagram")}`,
            };
            $pm.resolveControl(
                this,
                "TrendPrint",
                () => this._printChart(),
                options
            );
        } else this._printChart();
    }

    _printChart() {
        const svg = this.shadowRoot.querySelector("svg")
        const b64svg = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg.outerHTML)))}`
        const template = generatePrintTemplatre(b64svg);
        if (window.ipc && window.clientPrinter) {
            window.ipc
                .get()
                .send(
                    "server",
                    "print",
                    `data:text/html;charset=utf8,${template}`
                );
            return;
        }
        const iframe = document.createElement("iframe");
        iframe.onload = () => {
            let printDocument = iframe.contentWindow || iframe.contentDocument;
            if (printDocument.document) printDocument = printDocument.document;
            requestAnimationFrame(() => {
                iframe.focus();
                iframe.contentWindow.print();
                iframe.remove();
            })
        }
        iframe.srcdoc = template;
        document.body.appendChild(iframe);
    }

    downloadImage() {
        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.savePath
                ? `${i18next.t("chartcolumn_export_data")} : ${this.elname} (${window.savePath}${fileName})${window.saveDeviceDesc}`
                : `${i18next.t("chartcolumn_export_data")} : ${this.elname}`,
            };
            $pm.resolveControl(
                this,
                "TrendSave",
                () => this._downloadImage(),
                options
            );
        } else this._downloadImage();
    }

    _downloadImage() {
        const fileName =
            generateFileName(
                this.exportnameformat,
                this.elname,
                this.parentElement.getAttribute("objectid")
            ) + ".png";
        const svg = this.shadowRoot.querySelector("svg")
        const canvas = document.createElement("canvas");
        const h = svg.clientHeight * 2
        const w = svg.clientWidth * 2
        canvas.width = w;
        canvas.height = h;
        const context = canvas.getContext("2d");
        context.fillStyle = "#fff";
        context.fillRect(0, 0, w, h);
        const image = new Image(w, h);
        image.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg.outerHTML)))}`
        image.onload = () => {
            context.drawImage(image, 0, 0, w, h);
            const link = document.createElement("a");
            link.download = fileName;
            link.href = canvas.toDataURL("image/png");
            link.click();
        };
    }

    updateEnableState(isenabled) {
        this.root.style.pointerEvents = isenabled ? "auto" : "none";
    }

    attributeChangedCallback(attrName, oldVal, newVal) {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        if (attrName === 'width') this.initChart();
        if (attrName === 'height') this.initChart();
    }

    connectedCallback() {
        if (this.state.saveStateFlag === undefined) this.state.saveStateFlag = this.calcSaveState();
        this.initWorker()
    }

    setDefaultGroupState() {
        const path = this.getOriginPath();
        const storage = $ss.getItemState(path);
        if(!storage || !storage.groups) return
        const groups = storage.groups.map(g => ({ ...g, component: this.groups.createGroupComponent(g.id, g.name) }))
        this.state.groups.push(...groups)
        this.groups.values = this.state.groups
        if (storage?.activeGroupId && storage.activeGroupId === 'default'){
            const selectedGroup = this.state.groups.find(g => g.id === 'default')
            this.state.selectedSources = selectedGroup.selected.map(g => g.source)
            this.state.selectedGroup = 'default'
            this.groups.value = 'default'
        } else{
            const exist = storage.groups.find(g => g.id === storage.activeGroupId)
            if (!exist) return
            this.state.selectedSources = exist.selected.map(g => g.source)
            this.state.selectedGroup = storage.activeGroupId
            this.groups.value = storage.activeGroupId
        }
    }

    setDefaultIntervalState() {
        const path = this.getOriginPath();
        const storage = $ss.getItemState(path);
        if (!storage) return
        if (storage.division) this.state.division = storage.division
        if (storage.startDate) this.state.archivestart = storage.startDate
        if (storage.endDate) this.state.archiveend = storage.endDate
    }

    changePenByName(pen, parameter, value){
        if(!pen) return
        switch(parameter){
            case "Visibility":
                const pName = pen.name
                pen.active = value
                this.state.echarts.legend.selected[pName] = value
                this.echarts.dispatchAction({
                    type: value ? 'legendSelect' : 'legendUnSelect',
                    name: pName
                })
                break;
            case "Color":
                pen.serie.itemStyle.color = value
                this.updateChart()
            case "DataType":
                pen.datatype = value
                this.getHistoryData()
        }
        this.menu?.pensElem?.render()
    }

    changePenAttribute(attr, value) {
        let index = +attr.split(".")[1];
        if (Number.isNaN(index) || !this.state.pens[index]) {
            return;
        }
        let parameter = attr.split(".")[2];
        if(index !== undefined && parameter){
            const pen = this.state.pens[index]
            this.changePenByName(pen, parameter, value)
        }
    }

    async afterInitialize() {
        if (!this.state.initialized) return this.state.initialized = true
        this.initializeAndFetchHistory();
        this.setPm();
    }

    async setDefaultGroupPathes() {
        await this.getSourcePath()
        const currentPens = this.state.pens
            .map(({ serie, ...rest }) => rest)
        const currentGroup = this.state.groups.find(g => g.id === this.state.selectedGroup)
        currentGroup.selected.push(...currentPens)
    }

    disconnectedCallback(){
        window.removeEventListener("ms_message", this.afterSwitch);
        super.disconnectedCallback();
    }

    get height() {
        return super.height;
    }

    set height(value) {
        if (value === undefined || value === super.height) return
        super.height = +value;
        setTimeout(() => {
            if (this.echarts) this.echarts.resize({ height: +value })
        }, 230) // таймер нужен, чтобы компонент успел обновить высоту
    }

    get width() {
        return super.width;
    }
    set width(value) {
        if (value === undefined || value === super.width) return
        super.width = value;
        setTimeout(() => {
            if (this.echarts) this.echarts.resize({ width: value})
        }, 230)
    }

    get resampleInterval() {
        return this.state.resampleInterval;
    }
    set resampleInterval(v) {
        this.state.resampleInterval = v
    }

    /**
     * Подчеркивание подписи
    * */
    get underliningsignature() {
        return this.state.underliningsignature;
    }
    set underliningsignature(v) {
        if (v === 'false') this.state.underliningsignature = false;
        else this.state.underliningsignature = true;
        if (this.state.underliningsignature && this.state.cache.echarts.yAxis.axisLabel.rich === undefined) {
            this.state.cache.echarts.yAxis.axisLabel.formatter = ['{a|{value}}', '{b|}'].join('\n'),
                this.state.cache.echarts.yAxis.axisLabel.rich = {
                    a: {
                        padding: [0, 0, 3, 0]
                    },
                    b: {
                        borderType: 'solid',
                        borderWidth: 0.5,
                        borderColor: this.yaxiscolor,
                        height: 0.5,
                        width: '100%',
                    }
                }
            this.state.echarts.yAxis.axisLabel = structuredClone(this.state.cache.echarts.yAxis.axisLabel)
            this.echarts.setOption({ yAxis: { axisLabel: this.state.echarts.yAxis.axisLabel } })
        }
    }

    /**
     * Курсив подписи
    * */
    get italicsignature() {
        return this.state.italicsignature;
    }
    set italicsignature(v) {
        if (v === 'false') this.state.italicsignature = false;
        else this.state.italicsignature = true;
        if (this.state.italicsignature && !this.state.cache.echarts.yAxis.axisLabel.fontStyle) {
            if (this.state.echarts.yAxis.axisLabel.rich) {
                this.state.echarts.yAxis.axisLabel.rich.a.fontStyle = this.state.cache.echarts.yAxis.axisLabel.rich.a.fontStyle = 'italic'
            } else {
                this.state.echarts.yAxis.axisLabel.fontStyle = this.state.cache.echarts.yAxis.axisLabel.fontStyle = 'italic'
            }
            this.state.echarts.yAxis.axisLabel.fontStyle = this.state.cache.echarts.yAxis.axisLabel.fontStyle = 'italic'
            this.echarts.setOption({ yAxis: { axisLabel: this.state.echarts.yAxis.axisLabel } })
        }
    }

    /**
    * Права(Создание групп перьев)
    * */
    get pmPenGroupsCreate() {
        return this._pmPenGroupsCreate
    }

    set pmPenGroupsCreate(value){
        this._pmPenGroupsCreate = value;
        this.groups?.initOpenCreateBtn();
    }

    /**
    * Права(Настройка перьев)
    * */
    get pmPensSettings() {
        return this._pmPensSettings
    }

    set pmPensSettings(value){
        this._pmPensSettings = value
        this.menu?.render()
    }

    /**
    * Права(Настройка интервалов)
    * */
    get pmIntervalsSettings() {
        return this._pmIntervalsSettings
    }

    set pmIntervalsSettings(value){
        this._pmIntervalsSettings = value
        this.menu?.render()
    }

    /**
    * Права(Выбор стандартных интервалов)
    * */
    get pmStandardIntervals() {
        return this._pmStandardIntervals
    }

    set pmStandardIntervals(value){
        this._pmStandardIntervals = value
        if(this.intervals) this.intervals.dropdown.canOpen = value
    }

    /**
     * Права(Экспорт изображения)
    * */
    get pmImageExport() {
        return this._pmImageExport
    }

    set pmImageExport(value){
        this._pmImageExport = value
        this.menu?.render()
    }

    /**
    * Права(Печать диаграммы)
    * */
    get pmPrint() {
        return this._pmPrint
    }

    set pmPrint(value){
        this._pmPrint = value
        this.menu?.render()
    }

    /**
     * Максимум перьев для группы
    * */
    get penmaxammount() {
        return this.state.penmaxammount;
    }
    set penmaxammount(v) {
        const numV = +v
        if (!isNaN(numV)) this.state.penmaxammount = numV;
    }

    /**
     * Печать диаграммы
    * */
    get chartprinting() {
        return this._chartprinting;
    }
    set chartprinting(v) {
        if (v === 'false') this._chartprinting = false;
        else this._chartprinting = true;
        this.menu?.render()
    }

    /**
     * Экспорт изображения
    * */
    get imageexport() {
        return this._imageexport;
    }
    set imageexport(v) {
        if (v === 'false') this._imageexport = false;
        else this._imageexport = true;
        this.menu?.render()
    }

    /**
     * Сброс настроек
    * */
    get resetsetup() {
        return this._resetsetup;
    }
    set resetsetup(v) {
        if (v === 'false') this._resetsetup = false;
        else this._resetsetup = true;
        this.menu?.render()
    }

    /**
     * Настройка внешнего вида
    * */
    get setupcustomization() {
        return this.state.setupcustomization;
    }
    set setupcustomization(v) {
        if (v === 'false') this.state.setupcustomization = false;
        else this.state.setupcustomization = true;
        this.menu?.render()
    }

    /**
     * Настройска вида значений
    * */
    get setupdatatype() {
        return this.state.setupdatatype;
    }
    set setupdatatype(v) {
        if (v === 'false') this.state.setupdatatype = false;
        else this.state.setupdatatype = true;
        this.menu?.render()
    }

    /**
     * Настройка интервала
    * */
    get setupinterval() {
        return this._setupinterval;
    }
    set setupinterval(v) {
        if (v === 'false') this._setupinterval = false;
        else this._setupinterval = true;
        this.menu?.render()
    }

    /**
     * Настройка группы перьев
    * */
    get setuppengroup() {
        return this._setuppengroup;
    }
    set setuppengroup(v) {
        if (v === 'false') this._setuppengroup = false;
        else this._setuppengroup = true;
        if (this.menu) this.menu.render()
    }

    /**
     * Имя группы
    * */
    get groupname() {
        const defaultGroup = this.state.groups.find(item => item.id === 'default')
        return defaultGroup.name
    }
    set groupname(v) {
        if (!v) return
        const defaultGroup = this.state.groups.find(item => item.id === 'default')
        defaultGroup.name = v
        defaultGroup.component = v
        if (!this.groups) return
        this.groups.labelElem.innerHTML = v
        this.groups.dropdown.createList()
        const navigation = this.shadowRoot.querySelector('#navigation')
        navigation.classList.add('active')
    }

    /**
     * Деление
     * 4 - Месяц
     * 3 - День
     * 2 - Час
     * 1 - Минута
     * 0 - Секунда
    * */
    get division() {
        return this.state.division
    }
    set division(v) {
        const values = [0, 1, 2, 3, 4];
        const numV = +v
        if (isNaN(numV) || !values.includes(numV)) return
        this.state.division = numV;
        if (this.state.cache.division === undefined) this.state.cache.division = numV
        switch(numV){
            case 4:
                this.formattime = "MM.yyyy";
                break;
            case 3:
                this.formattime = "dd.MM.yyyy";
                break;
            case 2:
                this.formattime = "dd.MM.yyyy HH:mm";
                break;
            default:
                this.formattime = "dd.MM.yyyy";
                break
        }
    }

    /**
     * Начальное значение
    * */
    get archivestart() {
        return this.state.archivestart
    }
    set archivestart(v) {
        if (isNaN(+v)) return
        if(+v !== 0){
            this.state.archivestart = +new Date(+v)
            if (this.state.cache.archivestart === undefined) this.state.cache.archivestart = +v
        } else {
            const date = new Date();
            this.state.archivestart = +new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6, 0)
        }
    }

    /**
     * Конечное значение
    * */
    get archiveend() {
        return this.state.archiveend
    }
    set archiveend(v) {
        if (isNaN(+v)) return
        if(+v !== 0){
            this.state.archiveend = +new Date(+v)
            if (this.state.cache.archiveend === undefined) this.state.cache.archiveend = +v
        } else {
            const date = new Date();
            this.state.archiveend = +new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)
        }
    }

    /**
     * Столбцы диаграммы
    * */
    get data() {
        return this.state.pens
    }
    set data(v) {
        if(v){
            try {
                const parsedPens = JSON.parse(v);
                const keys = Object.keys(parsedPens)
                for (let key of keys) this.state.createPen(parsedPens[key])
                this.updateChart()
            } catch (e) {
                console.error('Error occured while parsing chart columns. ', e)
            }
        }
    }

    /**
     * Подпись оси Y
    * */
    get yaxislabel() {
        return this.state.echarts.yAxis.name
    }
    set yaxislabel(v) {
        this.state.echarts.yAxis.name = v;
        this.echarts.setOption({ yAxis: this.state.echarts.yAxis })
    }

    /**
     * Цвет оси Y
    * */
    get yaxiscolor() {
        return this.state.echarts.yAxis.axisLine.lineStyle.color
    }
    set yaxiscolor(v) {
        this.state.echarts.yAxis.axisLine.lineStyle.color = v
        if (this.state.cache.echarts.yAxis.axisLine.lineStyle.color === undefined) {
            this.state.cache.echarts.yAxis.axisLine.lineStyle.color = v
        }
        this.echarts.setOption({ yAxis: this.state.echarts.yAxis })
    }

    /**
     * Жирность оси X
    * */
    get xaxisboldness() {
        return this.state.xaxisboldness
    }
    set xaxisboldness(v) {
        if (v === 'false') this.state.xaxisboldness = false
        else this.state.xaxisboldness = true
        if (this.state.xaxisboldness && this.state.cache.echarts.xAxis.axisLabel.rich.a.fontWeight === undefined) {
            if (this.state.echarts.xAxis.axisLabel.rich) {
                this.state.echarts.xAxis.axisLabel.rich.a.fontWeight = this.state.cache.echarts.xAxis.axisLabel.rich.a.fontWeight = 'bold'
            } else {
                this.state.echarts.xAxis.axisLabel.fontWeight = this.state.cache.echarts.xAxis.axisLabel.fontWeight = 'bold'
            }
            this.state.echarts.xAxis.axisLabel.fontWeight = this.state.cache.echarts.xAxis.axisLabel.fontWeight = 'bold'
            this.echarts.setOption({ xAxis: { axisLabel: this.state.echarts.xAxis.axisLabel } })
        }
    }

    /**
         * Курсив оси X
        * */
    get italicxaxis() {
        return this.state.italicxaxis;
    }
    set italicxaxis(v) {
        if (v === 'true') this.state.italicxaxis = true
        else this.state.italicxaxis = false
        if (this.state.italicxaxis && !this.state.cache.echarts.xAxis.axisLabel.fontStyle) {
            if (this.state.echarts.xAxis.axisLabel.rich) {
                this.state.echarts.xAxis.axisLabel.rich.a.fontStyle = this.state.cache.echarts.xAxis.axisLabel.rich.a.fontStyle = 'italic'
            } else {
                this.state.echarts.xAxis.axisLabel.fontStyle = this.state.cache.echarts.xAxis.axisLabel.fontStyle = 'italic'
            }
            this.state.echarts.xAxis.axisLabel.fontStyle = this.state.cache.echarts.xAxis.axisLabel.fontStyle = 'italic'
            this.echarts.setOption({ xAxis: { axisLabel: this.state.echarts.xAxis.axisLabel } })
        }
    }

    /**
     * Цвет оси X
    * */
    get xaxiscolor() {
        return this.state.echarts.xAxis.axisLine.lineStyle.color
    }
    set xaxiscolor(v) {
        this.state.echarts.xAxis.axisLine.lineStyle.color = v
        if (this.state.cache.echarts.xAxis.axisLine.lineStyle.color === undefined) {
            this.state.cache.echarts.xAxis.axisLine.lineStyle.color = v
        }
        this.echarts.setOption({ xAxis: this.state.echarts.xAxis })
    }

    /**
     * Шкала значений отключает ось Y
    * */
    get yaxisactive() {
        return this.state.echarts.yAxis.show
    }
    set yaxisactive(v) {
        this.state.echarts.yAxis.show = v
        this.echarts.setOption({ yAxis: { show: v } })
    }

    /**
     * Автомасштабирование
    * */
    get autoscaling() {
        return this.state.autoscaling;
    }

    set autoscaling(v) {
        if (v === 'false') this.state.autoscaling = false
        else if (v === 'true') this.state.autoscaling = true
        else this.state.autoscaling = v
        if (this.state.cache.autoscaling === undefined) this.state.cache.autoscaling = this.state.autoscaling
        this.echarts.setOption({ yAxis: this.autoscaling ? { min: null, max: null } : { min: this.state.min, max: this.state.max } })
    }

    /**
     * Накопление
    * */
    get accumulation() {
        return this.state.accumulation
    }
    set accumulation(v) {
        if (v === 'false') this.state.accumulation = false
        else if (v === 'true') this.state.accumulation = true
        else this.state.accumulation = v
        this.state.setupAccumulation()
        this.echarts.setOption({ series: this.state.echarts.series })
    }

    /**
     * Min
    * */
    get min() {
        return this.state.echarts.yAxis.min
    }
    set min(v) {
        const numV = +v
        if (isNaN(numV)) return
        this.state.min = numV;
        if (this.state.cache.min === undefined) this.state.cache.min = numV
        if (!this.autoscaling) this.echarts.setOption({ yAxis: { min: this.state.min } })
    }

    /**
     * Max
    * */
    get max() {
        return this.state.echarts.yAxis.max
    }
    set max(v) {
        const numV = +v
        if (isNaN(numV)) return
        this.state.max = numV;
        if (this.state.cache.max === undefined) this.state.cache.max = numV
        if (!this.autoscaling) this.echarts.setOption({ yAxis: { max: this.state.max } })
    }

    /**
     * Заливка области построения
    * */
    get fillingconstructionarea() {
        return this.state.echarts.grid.backgroundColor
    }
    set fillingconstructionarea(v) {
        this.state.echarts.grid.backgroundColor = v
        this.state.echarts.grid.show = true
        if (this.state.cache.echarts.grid.backgroundColor === undefined) {
            this.state.cache.echarts.grid.backgroundColor = v
            this.state.cache.echarts.grid.show = true
        }
        this.echarts.setOption({ grid: this.state.echarts.grid });
    }

    /**
     * Сетка фона
    * */
    get backgroundgrid() {
        return this.state.echarts.yAxis.splitLine.show
    }
    set backgroundgrid(v) {
        if (v === 'false'){
            this.state.echarts.yAxis.splitLine.show = false
            if(this.menu) this.menu.setTreeValue(false, 0)
        }
        else if (v === 'true') {
            this.state.echarts.yAxis.splitLine.show = true
            if(this.menu) this.menu.setTreeValue(true, 0)
        }
        else this.state.echarts.yAxis.splitLine.show = v
        if (!this.state.initialized) {
            this.state.cache.echarts.yAxis.splitLine.show = this.state.echarts.yAxis.splitLine.show
        }
        this.echarts.setOption({ yAxis: this.state.echarts.yAxis })
    }

    /**
     * Цвет сетки
    * */
    get gridcolor() {
        return this.state.echarts.yAxis.splitLine.lineStyle.color
    }
    set gridcolor(v) {
        this.state.echarts.yAxis.splitLine.lineStyle.color = v
        if (this.state.cache.echarts.yAxis.splitLine.lineStyle.color === undefined) {
            this.state.cache.echarts.yAxis.splitLine.lineStyle.color = v
        }
        this.echarts.setOption({ yAxis: this.state.echarts.yAxis });
    }

    /**
     * Включить меню
    * */
    get menuonoff() {
        return this._menuonoff;
    }
    set menuonoff(v) {
        if (v === 'false') this._menuonoff = false
        else this._menuonoff = true
        this.initMenu()
    }

    /**
     * Список стандартных интервалов
    * */
    get standardlistintervals() {
        return this._standardlistintervals;
    }
    set standardlistintervals(v) {
        if (v === 'false') this._standardlistintervals = false
        else this._standardlistintervals = true
        this.initIntervals()
    }

    /**
     * Список групп перьев
    * */
    get pengroupslist() {
        return this._pengroupslist;
    }
    set pengroupslist(v) {
        if (v === 'false') this._pengroupslist = false
        else this._pengroupslist = true
        this.initGroups()
    }

    /**
     * Легенда
    * */
    get legend() {
        return this.state.echarts.legend.show
    }
    set legend(v) {
        if (v === 'false') this.state.echarts.legend.show = false
        else this.state.echarts.legend.show = true
        this.updateChart()
    }

    /**
     * Формат имени экспорта
    * */
    get exportnameformat() {
        return (
            this._exportNameFormat || "{Name}_{yyyy}-{MM}-{dd}-{HH}_{mm}_{ss}"
        );
    }
    set exportnameformat(value) {
        this._exportNameFormat = value;
    }

    /**
     * Формат времени в таблице
    * */
    get formattime() {
        return _formattime;
    }
    set formattime(v) {
        this._formattime = v;
        this.state.echarts.xAxis.axisPointer.label.formatter = function ({ value }) {
            if (!value) return
            return getFormattedValue(v, new Date(+value)).replace(" ", "\n")
        }
        this.state.echarts.xAxis.axisLabel.formatter = function (value) {
            return getFormattedValue(v, new Date(+value)).replace(" ", "\n")
        }
    }
}
